import { Component, Input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { CustomerJourneyTimelineComponent } from '@app/customer/home/customer-journey-timeline.component';
import { CustomerHomeJourney } from '@app/customer/shared/customer-home.model';
import { ContactShowRound } from '@app/contacts/shared/contact-show-round.model';

/**
 * TVG-360 section eleven (a and b): what is true of the *contact*, shown once,
 * directly under the contact block and above show rounds.
 *
 * Two things sit here, both of which previously had nowhere to live:
 *
 *   a. The sales journey. It describes the contact's progress as a customer,
 *      not the progress of one event, and a contact can only ever be at one
 *      point in it — so it reads once here rather than once inside every
 *      expanded event. The steps and how they are lit are unchanged; the
 *      server resolves which single stage the contact is at.
 *   b. The venues this contact has a viewing booked at, and when. One compact
 *      line, not a table, so a salesperson can see at a glance that someone is
 *      viewing more than one venue. The full show rounds panel below still
 *      carries the detail and the history.
 *
 * Purely presentational: both inputs are handed down by the contact page from
 * data the panels below already fetch, so nothing here asks the server for
 * anything a second time.
 */
@Component({
  selector: 'app-contact-journey-summary',
  standalone: true,
  imports: [RouterLink, CustomerJourneyTimelineComponent],
  templateUrl: './contact-journey-summary.component.html',
  /**
   * Eight steps across a phone screen leaves about forty pixels a column, and
   * the shared timeline's step labels are wider than that, so on a narrow
   * screen they run into one another. The numbered and ticked nodes carry the
   * progress perfectly well on their own, and the line directly beneath the
   * stepper already names the step the contact is at in words — so below the
   * small breakpoint the labels simply step aside.
   *
   * Deliberately scoped to this card rather than fixed in the timeline itself:
   * the same component renders the customer-facing home, which is a separate
   * case and must not change.
   */
  styles: [
    `
      @media (max-width: 639px) {
        :host ::ng-deep app-customer-journey-timeline button > span.journey-step-label {
          display: none;
        }
      }
    `,
  ],
})
export class ContactJourneySummaryComponent {
  /** The contact's single journey, or null while it loads / when there is none. */
  @Input() journey: CustomerHomeJourney | null = null;

  /** Every show round on the contact's events, booked or already happened. */
  @Input() showRounds: ContactShowRound[] = [];

  /**
   * The viewings that are actually *booked* — still to come. A round that has
   * been turned up to, cancelled or missed is history and belongs to the panel
   * below, not to a line whose whole point is "this person is going to see
   * these venues". Ordered by date so the next viewing reads first.
   */
  protected get bookedViewings(): ContactShowRound[] {
    return this.showRounds
      .filter((round) => round.outcome === 'booked')
      .slice()
      .sort((a, b) => (a.date ?? '').localeCompare(b.date ?? ''));
  }

  /** How many distinct venues those bookings are at — the point of the line. */
  protected get bookedVenueCount(): number {
    return new Set(this.bookedViewings.map((round) => round.venue?.name ?? 'Venue TBC')).size;
  }

  /**
   * A viewing date reads short — "Sat 24 Oct 2026" — because several sit on one
   * line. The panel below spells the same dates out in full.
   */
  protected formatShortDate(value: string | null): string {
    if (!value) {
      return 'Date TBC';
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    // Assembled from the parts, the way every other date on this page is, so
    // it reads "Sat 14 Nov 2026" without the locale's comma after the day.
    const parts = new Intl.DateTimeFormat('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).formatToParts(parsed);
    const part = (type: string): string => parts.find((p) => p.type === type)?.value ?? '';

    return `${part('weekday')} ${part('day')} ${part('month')} ${part('year')}`.trim();
  }
}
